import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { log } from '../log/log';
import type { Message } from '../sync/persistence';

export type MessagesState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'populated'; messages: Message[] }
  | { kind: 'error'; error: Error };

export function useMessages(conversationId: string): MessagesState {
  const [state, setState] = useState<MessagesState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const bootstrap = async () => {
      try {
        log.debug('useMessages.bootstrap', { conversationIdPrefix: conversationId.slice(0, 6) });

        const adapter =
          Platform.OS === 'web'
            ? (await import('../sync/messagesAdapter.web')).createWebMessagesAdapter()
            : (await import('../sync/messagesAdapter.macos')).createMacosMessagesAdapter();

        // Sort ASCENDING by wall-clock timestamp (oldest first, newest
        // last) so the ChatRoom renderer puts the latest bubble at
        // the bottom of the scroll view and `scrollToEnd` lands on it.
        //
        // Why timestamp-first: HLC is a string ("wall_ms.logical.device")
        // and lexicographic order disagrees with chronological order
        // whenever wall_ms is unpadded — e.g. "99…" sorts AFTER "100…"
        // even though 99 < 100. Putting numeric timestamp first
        // guarantees chronological ordering; the HLC tiebreaker only
        // matters for the rare case of two messages sharing a
        // millisecond, where lexicographic-on-HLC is a stable order.
        const sortMessages = (input: readonly Message[]): Message[] =>
          [...input].sort((a, b) => {
            if (a.timestamp !== b.timestamp) {
              return a.timestamp - b.timestamp;
            }
            return a.hlc.localeCompare(b.hlc);
          });

        unsubscribe = adapter.subscribeToMessages(conversationId, (messages: Message[]) => {
          if (!cancelled) {
            if (messages.length === 0) {
              setState({ kind: 'empty' });
            } else {
              setState({ kind: 'populated', messages: sortMessages(messages) });
            }
          }
        });

        const initialMessages = await adapter.getMessages(conversationId);
        if (!cancelled) {
          if (initialMessages.length === 0) {
            setState({ kind: 'empty' });
          } else {
            setState({ kind: 'populated', messages: sortMessages(initialMessages) });
          }
        }
      } catch (error) {
        if (!cancelled) {
          log.error('useMessages.bootstrap failed', error as Error);
          setState({ kind: 'error', error: error as Error });
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, [conversationId]);

  return state;
}
